import { mergeOptions } from '../../helpers/common/merge-options';
import { Loader } from '@googlemaps/js-api-loader';

interface MapMarkerExtended {
	marker: google.maps.Marker;
	className: string;
}

export interface MapExtendedOptions {
	zoom: number;
	mapCenter?: {
		lat: number;
		lng: number;
	};
	draggable: boolean;
	urlLocation?: string;
	urlDatacenter?: string;
	animation: any; //google.maps.Animation;
	mapStyles?: google.maps.MapTypeStyle[];
	styledMapName: string;
	mapMarkerClasses: string;
	showMapMarkerClasses: string;
	hasMapMarkerClassesButtonIfMultiple: boolean;
	mapMarkerClassesButtonActiveClass: string;
	mapMarkerInfoClass: string;
	mapTypes: (google.maps.MapTypeId | string)[];
	mapTypeControlStyle: any; //google.maps.MapTypeControlStyle;
	fitToBounds: boolean;
	enforceZoomLevel: boolean;
	mapApiKey: string;
	nonce?: string;
}

export class MapExtended {
	static options: MapExtendedOptions = {
		zoom: 11,
		mapCenter: { lat: 46.905358, lng: 8.423689 },
		draggable: screen.width < 1025 === false,
		animation: 2, //google.maps.Animation.DROP,
		mapMarkerClasses: 'map-location',
		showMapMarkerClasses: 'map-location',
		hasMapMarkerClassesButtonIfMultiple: false,
		mapMarkerClassesButtonActiveClass: 'is-active',
		mapMarkerInfoClass: 'c-map-extended__information__marker',
		mapTypes: ['roadmap', 'terrain', 'satellite', 'hybrid'],
		mapTypeControlStyle: 1, //google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
		fitToBounds: true,
		styledMapName: 'Map',
		enforceZoomLevel: false,
		mapApiKey: ''
	};
	private options: MapExtendedOptions;
	private mapMarkers: MapMarkerExtended[] = [];
	private currentInfoWindow: any = null;
	private bounds?: google.maps.LatLngBounds;

	constructor(private element: HTMLElement, options?: Partial<MapExtendedOptions>) {
		this.options = mergeOptions(MapExtended, options, element);

		new Loader({
			apiKey: this.options.mapApiKey,
			version: 'weekly',
			libraries: ['marker'],
			nonce: this.options.nonce,
			language: document.documentElement.getAttribute('lang')
		})
			.load()
			.then(google => {
				window.google = google;
				this.init();
			});
	}

	init() {
		const mapMarkerInformations = Array.from(this.element.getElementsByClassName('c-map-extended__information'));
		let mapMarkerClasses: string[] = [];
		if (mapMarkerInformations != null) {
			mapMarkerInformations.forEach(e => {
				mapMarkerClasses.push(e.getAttribute('data-marker-class') ?? '');
			});
			mapMarkerClasses = mapMarkerClasses.filter((v, i, a) => a.indexOf(v) === i);
		}

		this.bounds = new google.maps.LatLngBounds();
		if (this.options.mapStyles != null && this.options.mapTypes.indexOf('map_style') < 0) {
			this.options.mapTypes.push('map_style');
		}
		const id = this.element.getAttribute('data-map-id');

		if(!id) {
			throw "Map ID is not defined";
		}
		const map = new google.maps.Map(document.getElementById(id), {
			zoom: +this.options.zoom,
			center: this.options.mapCenter,
			draggable: true,
			mapTypeControlOptions: {
				mapTypeIds: this.options.mapTypes,
				style: this.options.mapTypeControlStyle
			}
		});

		if (this.options.mapStyles) {
			map.mapTypes.set(
				'map_style',
				new google.maps.StyledMapType(this.options.mapStyles, {
					name: this.options.styledMapName
				})
			);
			map.setMapTypeId('map_style');
		}

		this.options.mapMarkerClasses?.split(',').forEach(markerClass => {
			const showMarker = this.options.showMapMarkerClasses?.split(',').indexOf(markerClass) > -1;
			Array.from(this.element.getElementsByClassName(markerClass)).forEach(markerClassInfo =>
				this.createMarkerClass(markerClassInfo as HTMLElement, markerClass, map, showMarker)
			);
		});

		if (this.options.fitToBounds) {
			map.fitBounds(this.bounds);
		}
		if (this.options.zoom && this.options.enforceZoomLevel) {
			google.maps.event.addListenerOnce(map, 'idle', () => {
				map.setZoom(+this.options.zoom);
			});
		}
		if (this.options.hasMapMarkerClassesButtonIfMultiple && this.options.mapMarkerClasses.length > 1) {
			this.options.mapMarkerClasses
				?.split(',')
				.map(markerClass => document.getElementById(markerClass))
				.forEach(markerElement =>
					markerElement?.addEventListener('click', () => {
						markerElement.classList.toggle(this.options.mapMarkerClassesButtonActiveClass);
						const visible = markerElement.classList.contains(this.options.mapMarkerClassesButtonActiveClass);
						this.mapMarkers.filter(mapMarker => mapMarker.className === markerElement.id).forEach(mapMarker => mapMarker.marker.setVisible(visible));
					})
				);
		}
	}

	createMarkerClass(markerInfoData: HTMLElement, markerClass: string, map: any, showMarker: boolean) {
		const { lat, lng, marker, markerWidth, markerHeight, markerOriginX, markerOriginY, markerAnchorX, markerAnchorY } = markerInfoData.dataset;

		const mapMarkerInfoArray = Array.from(markerInfoData.getElementsByClassName(this.options.mapMarkerInfoClass));
		let mapMarkerInfo = '';
		if (mapMarkerInfoArray.length > 0) {
			mapMarkerInfo = markerInfoData.innerHTML;
		}

		const markerEl = new google.maps.Marker({
			position: new google.maps.LatLng(+parseFloat(lat ?? '0'), +parseFloat(lng ?? '0')),
			map,
			icon: marker
				? {
						size: new google.maps.Size(+(markerWidth ?? 0), +(markerHeight ?? 0)),
						origin: new google.maps.Point(+(markerOriginX ?? 0), +(markerOriginY ?? 0)),
						anchor: new google.maps.Point(+(markerAnchorX ?? 0), +(markerAnchorY ?? 0)),
						url: marker
				  }
				: null,
			visible: showMarker
		});
		if (this.bounds) {
			this.bounds.extend(markerEl.getPosition()!);
		}
		if (mapMarkerInfo !== '') {
			const infoWindow = new google.maps.InfoWindow({
				content: mapMarkerInfo
			});
			markerEl.addListener('click', () => {
				const wasCurrentWindow = this.currentInfoWindow != null && this.currentInfoWindow === infoWindow;
				if (this.currentInfoWindow != null) {
					this.currentInfoWindow.close();
				}
				if (this.currentInfoWindow == null || this.currentInfoWindow !== infoWindow) {
					infoWindow.open(map, markerEl);
					this.currentInfoWindow = infoWindow;
				} else if (wasCurrentWindow) {
					this.currentInfoWindow = null;
				}
			});
			markerEl.addListener('mouseover', () => {
				if (this.currentInfoWindow != null && this.currentInfoWindow !== infoWindow) {
					this.currentInfoWindow.close();
				}
				if (this.currentInfoWindow == null || this.currentInfoWindow !== infoWindow || this.mapMarkers.length == 1) {
					infoWindow.open(map, markerEl);
					this.currentInfoWindow = infoWindow;
				}
			});
		}
		this.mapMarkers.push({ marker: markerEl, className: markerClass });
	}
}
